import { useState } from 'react';
import { useDebouncedCallback } from '@helpers/useDebounce';
import axios from '@helpers/axios';
import theme from '@theme/sbot.theme';
import type { ErrorMessage, Dict } from '@theme/theme.d';

export const useFuzzySearch = <T>(endpoint: string) => {
  const [debouncing, setDebouncing] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage>();
  const [loading, setLoading] = useState<boolean>(false);
  const [results, setResults] = useState<T | null>(null);
  const [searchQuery, setSearchQuery] = useState<Dict>();

  const search = async (queryObj: Dict) => {
    setSearchQuery(queryObj);
    setDebouncing(true);
    fuzzySearch(queryObj);
  };

  const makeCall = async (queryObj: Dict) => {
    setLoading(true);
    await axios
      .get(`${endpoint}`, {
        params: queryObj,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      .then((response) => {
        if (response.status === 200) {
          setResults(response.data);
          setLoading(false);
        }
      })
      .catch((e) => {
        setError(e as ErrorMessage);
      });
  };

  const fuzzySearch = useDebouncedCallback(
    (text) => {
      setResults(null);
      setDebouncing(false);
      if (text) {
        makeCall(searchQuery as Dict);
      }
    },
    theme.global?.debounceDelay as number,
    { leading: false, trailing: true }
  );

  const onClearResults = () => {
    setResults(null);
  };

  return {
    debouncing,
    error,
    loading,
    onClearResults,
    results,
    search,
  };
};
